/**
 * The signed-in Jupiter theme: graphite on near-black, hairline borders instead of shadows, one warm accent taken
 * from the planet (ochre) and one cool accent for live / connected states. Text colors are chosen for WCAG AA on
 * the paper surface.
 */
import { alpha, createTheme } from "@mui/material/styles";

export const fonts = {
  display: `"Helvetica Neue", Helvetica, Arial, system-ui, sans-serif`,
  body: `"Inter", "Helvetica Neue", Helvetica, Arial, system-ui, sans-serif`,
  mono: `"JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace`,
};

export const tones = {
  ink: "#060709",
  paper: "#111317",
  raised: "#171a1f",
  line: "rgba(255,255,255,0.08)",
  lineStrong: "rgba(255,255,255,0.16)",
  ochre: "#d9a25f",
  ochreDeep: "#a8743a",
  live: "#6fd3df",
  ok: "#86d29e",
  warn: "#e8b85a",
  fail: "#f07e70",
  text: "#ece9e4",
  textDim: "rgba(236,233,228,0.66)",
};

/** Uppercase, widely spaced labels, the landing page's voice. */
export const caps = { fontFamily: fonts.display, textTransform: "uppercase" as const, letterSpacing: "0.22em" };

// A faint star field: a few one-pixel points tiled over a dark vignette with a warm glow off the top right, where the
// planet sits on the landing page.
const starDust = [
  "radial-gradient(1px 1px at 23px 41px, rgba(255,255,255,0.28), transparent 60%)",
  "radial-gradient(1px 1px at 141px 97px, rgba(255,255,255,0.18), transparent 60%)",
  "radial-gradient(1px 1px at 211px 13px, rgba(255,240,220,0.22), transparent 60%)",
  "radial-gradient(1px 1px at 77px 183px, rgba(255,255,255,0.14), transparent 60%)",
  "radial-gradient(1.5px 1.5px at 181px 211px, rgba(255,255,255,0.2), transparent 60%)",
].join(",");

const sky = [
  "radial-gradient(1100px 620px at 92% -12%, rgba(217,162,95,0.10), transparent 62%)",
  "radial-gradient(900px 520px at -8% 112%, rgba(111,211,223,0.045), transparent 60%)",
  "radial-gradient(ellipse at 50% 40%, transparent 55%, rgba(0,0,0,0.55) 100%)",
].join(",");

const base = createTheme({
  palette: {
    mode: "dark",
    primary: { main: tones.ochre, light: "#ecc28c", dark: tones.ochreDeep, contrastText: "#1a1209" },
    secondary: { main: tones.live, light: "#a3e6ee", dark: "#3ba4b2", contrastText: "#06191c" },
    success: { main: tones.ok, contrastText: "#0b1a10" },
    warning: { main: tones.warn, contrastText: "#1d1405" },
    error: { main: tones.fail, contrastText: "#1f0806" },
    info: { main: "#8ab8ea", contrastText: "#07121f" },
    background: { default: tones.ink, paper: tones.paper },
    text: { primary: tones.text, secondary: tones.textDim, disabled: "rgba(236,233,228,0.38)" },
    divider: tones.line,
    action: { hover: "rgba(255,255,255,0.045)", selected: alpha(tones.ochre, 0.1) },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: fonts.body,
    fontSize: 14,
    h4: { fontFamily: fonts.display, fontWeight: 200, letterSpacing: "0.04em" },
    h5: { ...caps, fontWeight: 300, fontSize: "1.35rem", letterSpacing: "0.28em" },
    h6: { fontFamily: fonts.display, fontWeight: 400, fontSize: "1.1rem", letterSpacing: "0.02em" },
    subtitle1: { fontWeight: 500 },
    overline: { ...caps, fontSize: "0.68rem", fontWeight: 500, lineHeight: 2 },
    button: { ...caps, fontSize: "0.72rem", fontWeight: 500, letterSpacing: "0.14em" },
    caption: { letterSpacing: "0.01em" },
  },
});

export const theme = createTheme(base, {
  components: {
    // Space Stack children with CSS gap. The default margin-based spacing resets each child's margins, which cancels
    // the negative margin a Grid container needs and shifts card grids to the right of the content edge.
    MuiStack: { defaultProps: { useFlexGap: true } },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: tones.ink,
          backgroundImage: `${sky}, ${starDust}`,
          backgroundSize: "100% 100%, 100% 100%, 100% 100%, 240px 240px, 240px 240px, 240px 240px, 240px 240px, 240px 240px",
          backgroundAttachment: "fixed",
          minHeight: "100vh",
          fontFeatureSettings: '"cv11", "ss01"',
        },
        "::selection": { background: alpha(tones.ochre, 0.35) },
        "*::-webkit-scrollbar": { width: 10, height: 10 },
        "*::-webkit-scrollbar-thumb": { background: "rgba(255,255,255,0.1)", borderRadius: 8, border: "2px solid transparent", backgroundClip: "padding-box" },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: { backgroundImage: "none", backgroundColor: alpha(tones.paper, 0.86), border: `1px solid ${tones.line}` },
      },
    },
    MuiCard: { styleOverrides: { root: { borderRadius: 14 } } },
    MuiDialog: {
      styleOverrides: {
        paper: { backgroundColor: tones.raised, borderColor: tones.lineStrong, borderRadius: 16, boxShadow: "0 30px 80px rgba(0,0,0,0.6)" },
      },
    },
    MuiDialogTitle: { styleOverrides: { root: { ...caps, fontSize: "0.85rem", letterSpacing: "0.2em", fontWeight: 500 } } },
    MuiPopover: { styleOverrides: { paper: { backgroundColor: tones.raised, borderColor: tones.lineStrong } } },
    MuiMenu: { styleOverrides: { paper: { backgroundColor: tones.raised, borderColor: tones.lineStrong } } },
    MuiAppBar: {
      styleOverrides: {
        root: { backgroundColor: alpha(tones.ink, 0.72), backgroundImage: "none", backdropFilter: "blur(10px)", border: 0, borderBottom: `1px solid ${tones.line}` },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 999, paddingInline: 16, minHeight: 34 },
        sizeSmall: { paddingInline: 12, minHeight: 28, fontSize: "0.66rem" },
        sizeLarge: { minHeight: 52, fontSize: "0.8rem" },
        containedPrimary: {
          background: `linear-gradient(180deg, #e3b276 0%, ${tones.ochre} 55%, #c98f4d 100%)`,
          boxShadow: `0 0 0 1px ${alpha(tones.ochre, 0.4)}, 0 6px 24px ${alpha(tones.ochre, 0.18)}`,
          "&:hover": { background: `linear-gradient(180deg, #ecc08a 0%, #e0ad6d 55%, ${tones.ochre} 100%)` },
        },
        outlined: { borderColor: tones.lineStrong },
        outlinedPrimary: { borderColor: alpha(tones.ochre, 0.5) },
        startIcon: { marginRight: 6 },
      },
    },
    MuiIconButton: { styleOverrides: { root: { borderRadius: 10 } } },
    MuiChip: {
      styleOverrides: {
        root: ({ ownerState }: { ownerState: { color?: string } }) => {
          const c = ownerState.color && ownerState.color !== "default" ? (base.palette as unknown as Record<string, { main: string }>)[ownerState.color]?.main : undefined;
          return {
            borderRadius: 6,
            fontWeight: 500,
            letterSpacing: "0.04em",
            backgroundColor: c ? alpha(c, 0.12) : "rgba(255,255,255,0.055)",
            color: c ?? tones.text,
            border: `1px solid ${c ? alpha(c, 0.38) : "rgba(255,255,255,0.12)"}`,
          };
        },
        sizeSmall: { height: 22, fontSize: "0.7rem" },
        label: { paddingInline: 8 },
        icon: { color: "inherit", marginLeft: 6 },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${tones.line}` },
        head: { ...caps, fontSize: "0.64rem", letterSpacing: "0.16em", color: tones.textDim, fontWeight: 500, paddingTop: 12, paddingBottom: 12 },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:last-child td": { borderBottom: 0 },
          "&.MuiTableRow-hover:hover": { backgroundColor: alpha(tones.ochre, 0.05) },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44, borderBottom: `1px solid ${tones.line}` },
        indicator: { height: 1, backgroundColor: tones.ochre, boxShadow: `0 0 10px ${tones.ochre}` },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { ...caps, fontSize: "0.68rem", letterSpacing: "0.18em", minHeight: 44, color: tones.textDim, "&.Mui-selected": { color: tones.text } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(0,0,0,0.25)",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.14)" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.28)" },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { backgroundColor: tones.raised, border: `1px solid ${tones.lineStrong}`, color: tones.text, fontSize: "0.72rem", maxWidth: 420, whiteSpace: "pre-wrap" },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 12, border: "1px solid", alignItems: "center" },
        standardError: { borderColor: alpha(tones.fail, 0.35), backgroundColor: alpha(tones.fail, 0.08) },
        standardWarning: { borderColor: alpha(tones.warn, 0.35), backgroundColor: alpha(tones.warn, 0.08) },
        standardInfo: { borderColor: alpha("#8ab8ea", 0.3), backgroundColor: alpha("#8ab8ea", 0.07) },
        standardSuccess: { borderColor: alpha(tones.ok, 0.3), backgroundColor: alpha(tones.ok, 0.07) },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          marginInline: 6,
          "&.Mui-selected": { backgroundColor: alpha(tones.ochre, 0.1), boxShadow: `inset 2px 0 0 ${tones.ochre}` },
        },
      },
    },
    MuiDivider: { styleOverrides: { root: { borderColor: tones.line } } },
    MuiLinearProgress: { styleOverrides: { root: { backgroundColor: "rgba(255,255,255,0.08)" } } },
  },
});
